import { useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import DatePickerAdvance from "@/components/DatePicker";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Download, 
  FileText, 
  DollarSign, 
  TrendingDown,
  Loader2,
  Printer
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const DatePicker = DatePickerAdvance;

type DateRange = {
  from?: Date;
  to?: Date;
};

interface StatData {
  amount: number;
  count: number;
}

export default function ExpensesReport() {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<DateRange | undefined>(undefined);
  const [endDate, setEndDate] = useState<DateRange | undefined>(undefined);
  const [selectedGroup, setSelectedGroup] = useState<string>("all");
  const [selectedBranch, setSelectedBranch] = useState<string>("all");
  const [selectedDistrict, setSelectedDistrict] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  // Fetch groups for filter
  const { data: groups = [] } = useQuery(["groups"], async () => {
    const { data } = await api.get("/groups");
    return data.results || [];
  });

  // Fetch branches for filter
  const { data: branches = [] } = useQuery(["branches"], async () => {
    const { data } = await api.get("/branches");
    return data.results || [];
  });

  // Fetch districts for filter
  const { data: districts = [] } = useQuery(["districts"], async () => {
    const { data } = await api.get("/districts");
    return data.results || [];
  });

  // Fetch expense categories
  const { data: categories = [] } = useQuery(["expense-categories"], async () => {
    const { data } = await api.get("/expense-categories");
    return data.results || [];
  });

  // Fetch expenses with filters
  const expensesQuery = useQuery({
    queryKey: ["expenses-report", { startDate, endDate, selectedGroup, selectedBranch, selectedDistrict, selectedCategory }],
    queryFn: async () => {
      const filters: any[] = [];

      if (selectedGroup !== "all") {
        filters.push({
          field: "groupId",
          operator: "eq",
          value: Number(selectedGroup),
        });
      }

      if (selectedBranch !== "all") {
        filters.push({
          field: "group.branch.name",
          operator: "eq",
          value: selectedBranch,
        });
      }

      if (selectedDistrict !== "all") {
        filters.push({
          field: "group.branch.district.name",
          operator: "eq",
          value: selectedDistrict,
        });
      }

      if (selectedCategory !== "all") {
        filters.push({
          field: "categoryId",
          operator: "eq",
          value: Number(selectedCategory),
        });
      }

      if (startDate?.from) {
        filters.push({
          field: "date",
          operator: "gte",
          value: format(startDate.from, "yyyy-MM-dd"),
        });
      }

      if (endDate?.to) {
        filters.push({
          field: "date",
          operator: "lte",
          value: format(endDate.to, "yyyy-MM-dd"),
        });
      }

      const { data } = await api.get("/expenses", {
        params: {
          page_size: 1000,
          page: 1,
          filters: filters.length > 0 ? filters : undefined,
        },
      });

      return data.results || [];
    },
  });

  const expenses = expensesQuery.data || [];

  // Calculate statistics
  const stats = {
    totalExpenses: expenses.length,
    totalAmount: expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0),
    uniqueGroups: new Set(expenses.map(e => e.group?.id).filter(Boolean)).size,
    uniqueCategories: new Set(expenses.map(e => e.category?.id).filter(Boolean)).size,
  };

  stats.totalAmount = Math.round(stats.totalAmount);

  // Group by category
  const categoryStats: Record<string, StatData> = expenses.reduce((acc, e) => {
    const category = e.category?.name || "Uncategorized";
    if (!acc[category]) {
      acc[category] = { count: 0, amount: 0 };
    }
    acc[category].count++;
    acc[category].amount += Number(e.amount) || 0;
    return acc;
  }, {} as Record<string, StatData>);

  // Group by group
  const groupStats: Record<string, StatData> = expenses.reduce((acc, e) => {
    const groupName = e.group?.name || "Unknown";
    if (!acc[groupName]) {
      acc[groupName] = { count: 0, amount: 0 };
    }
    acc[groupName].count++;
    acc[groupName].amount += Number(e.amount) || 0;
    return acc;
  }, {} as Record<string, StatData>);

  // Group by month
  const monthlyStats: Record<string, StatData> = expenses.reduce((acc, e) => {
    if (!e.date) return acc;
    const month = format(new Date(e.date), "MMM yyyy");
    if (!acc[month]) {
      acc[month] = { count: 0, amount: 0 };
    }
    acc[month].count++;
    acc[month].amount += Number(e.amount) || 0;
    return acc;
  }, {} as Record<string, StatData>);

  // Export to Excel
  const exportToExcel = () => {
    try {
      if (expenses.length === 0) {
        toast.error("No data to export");
        return;
      }

      const data = expenses.map((e) => ({
        Date: e.date ? format(new Date(e.date), "yyyy-MM-dd") : "",
        Description: e.description || "",
        Amount: Number(e.amount) || 0,
        Category: e.category?.name || "",
        Group: e.group?.name || "",
        Branch: e.group?.branch?.name || "",
        District: e.group?.branch?.district?.name || "",
        "Created By": e.createdBy?.name || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Expenses");

      const filename = `expenses-report-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      XLSX.writeFile(workbook, filename);
      toast.success("Excel file exported successfully");
    } catch (error: any) {
      toast.error("Failed to export Excel: " + (error.message || "Unknown error"));
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      if (expenses.length === 0) {
        toast.error("No data to export");
        return;
      }

      const doc = new jsPDF('landscape');
      const groupName = selectedGroup !== "all" 
        ? groups.find(g => g.id.toString() === selectedGroup)?.name || "All Groups"
        : "All Groups";

      // Header
      doc.setFontSize(16);
      doc.text(`Expenses Report - ${groupName}`, 14, 10);
      doc.setFontSize(12);
      if (startDate?.from && endDate?.to) {
        doc.text(
          `${format(startDate.from, "MMM dd, yyyy")} - ${format(endDate.to, "MMM dd, yyyy")}`,
          14,
          18
        );
      }
      doc.text(`Generated: ${format(new Date(), "MMM dd, yyyy")}`, 14, 24);

      // Summary
      doc.setFontSize(10);
      doc.text(`Total Expenses: ${stats.totalExpenses}`, 14, 32);
      doc.text(`Total Amount: ${stats.totalAmount.toLocaleString()} RWF`, 14, 38);
      doc.text(`Average Expense: ${stats.totalExpenses > 0 ? Math.round(stats.totalAmount / stats.totalExpenses).toLocaleString() : "0"} RWF`, 14, 44);
      doc.text(`Categories: ${stats.uniqueCategories}`, 14, 50);

      // Prepare table data
      const tableData = expenses.map((e) => [
        e.date ? format(new Date(e.date), "MMM dd, yyyy") : "",
        (e.description || "").substring(0, 30),
        e.category?.name || "",
        e.group?.name || "",
        e.group?.branch?.name || "",
        (Number(e.amount) || 0).toLocaleString(),
        e.createdBy?.name || "",
      ]);

      const headers = [
        "Date",
        "Description",
        "Category",
        "Group",
        "Branch",
        "Amount",
        "Created By",
      ];

      // Add table
      autoTable(doc as any, {
        head: [headers],
        body: tableData,
        startY: 55,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: 14, right: 14 },
      });

      const filename = `expenses-report-${format(new Date(), "yyyy-MM-dd")}.pdf`;
      doc.save(filename);
      toast.success("PDF file exported successfully");
    } catch (error: any) {
      toast.error("Failed to export PDF: " + (error.message || "Unknown error"));
    }
  };

  // Print report
  const handlePrint = () => {
    try {
      if (expenses.length === 0) {
        toast.error("No data to print");
        return;
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error("Please allow popups to print the report");
        return;
      }

      const groupName = selectedGroup !== "all" 
        ? groups.find(g => g.id.toString() === selectedGroup)?.name || "All Groups"
        : "All Groups";

      const dateRangeStr = startDate?.from && endDate?.to
        ? format(startDate.from, "MMM dd, yyyy") + " - " + format(endDate.to, "MMM dd, yyyy")
        : "All Time";

      const generatedDate = format(new Date(), "MMM dd, yyyy");
      const generatedTime = format(new Date(), "HH:mm:ss");
      
      const tableRows = expenses.map((e) => {
        const expenseDate = e.date ? format(new Date(e.date), "MMM dd, yyyy") : "—";
        const description = e.description || "—";
        const category = e.category?.name || "—";
        const groupName = e.group?.name || "—";
        const branchName = e.group?.branch?.name || "—";
        const amount = (Number(e.amount) || 0).toLocaleString();
        const createdBy = e.createdBy?.name || "—";
        return "<tr>" +
          "<td>" + expenseDate + "</td>" +
          "<td>" + description + "</td>" +
          "<td>" + category + "</td>" +
          "<td>" + groupName + "</td>" +
          "<td>" + branchName + "</td>" +
          "<td class=\"text-right text-red-600\">" + amount + "</td>" +
          "<td>" + createdBy + "</td>" +
          "</tr>";
      }).join('');

      const htmlContent = "<!DOCTYPE html>" +
        "<html>" +
        "<head>" +
        "<title>Expenses Report - " + groupName + "</title>" +
        "<style>" +
        "@media print { @page { size: landscape; margin: 1cm; } body { margin: 0; padding: 0; } }" +
        "body { font-family: Arial, sans-serif; padding: 20px; }" +
        ".header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }" +
        ".header h1 { margin: 0; font-size: 24px; color: #333; }" +
        ".header h2 { margin: 5px 0; font-size: 18px; color: #666; }" +
        ".summary { display: flex; justify-content: space-around; margin: 20px 0; padding: 15px; background-color: #f5f5f5; border-radius: 5px; }" +
        ".summary-item { text-align: center; }" +
        ".summary-item strong { display: block; font-size: 18px; color: #333; }" +
        ".summary-item span { font-size: 14px; color: #666; }" +
        "table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }" +
        "th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }" +
        "th { background-color: #3b82f6; color: white; font-weight: bold; text-align: center; }" +
        "tr:nth-child(even) { background-color: #f9fafb; }" +
        ".text-right { text-align: right; }" +
        ".text-red-600 { color: #dc2626; }" +
        ".footer { margin-top: 20px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }" +
        "</style>" +
        "</head>" +
        "<body>" +
        "<div class=\"header\">" +
        "<h1>Expenses Report - " + groupName + "</h1>" +
        "<h2>" + dateRangeStr + "</h2>" +
        "<p>Generated: " + generatedDate + "</p>" +
        "</div>" +
        "<div class=\"summary\">" +
        "<div class=\"summary-item\"><strong>" + stats.totalExpenses + "</strong><span>Total Expenses</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.totalAmount.toLocaleString() + " RWF</strong><span>Total Amount</span></div>" +
        "<div class=\"summary-item\"><strong>" + (stats.totalExpenses > 0 ? Math.round(stats.totalAmount / stats.totalExpenses).toLocaleString() : "0") + " RWF</strong><span>Average Expense</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.uniqueCategories + "</strong><span>Categories</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.uniqueGroups + "</strong><span>Groups</span></div>" +
        "</div>" +
        "<table>" +
        "<thead>" +
        "<tr><th>Date</th><th>Description</th><th>Category</th><th>Group</th><th>Branch</th><th class=\"text-right\">Amount</th><th>Created By</th></tr>" +
        "</thead>" +
        "<tbody>" + tableRows + "</tbody>" +
        "</table>" +
        "<div class=\"footer\">" +
        "<p>Expenses Report generated on " + generatedDate + " at " + generatedTime + "</p>" +
        "</div>" +
        "</body>" +
        "</html>";

      printWindow.document.write(htmlContent);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          toast.success("Print dialog opened");
        }, 250);
      };
    } catch (error: any) {
      toast.error("Failed to print: " + (error.message || "Unknown error"));
    }
  };

  const clearFilters = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectedGroup("all");
    setSelectedBranch("all");
    setSelectedDistrict("all");
    setSelectedCategory("all");
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {t("pages.expensesReport.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("pages.expensesReport.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportToPDF}
            disabled={expenses.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            {t("common.pdf")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToExcel}
            disabled={expenses.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            {t("common.excel")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={expenses.length === 0}
          >
            <Printer className="h-4 w-4 mr-1" />
            {t("common.print")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("pages.expensesReport.filters.title")}</CardTitle>
          <CardDescription>{t("pages.expensesReport.filters.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.expensesReport.filters.startDate")}</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate?.from && !startDate?.to && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate?.from && startDate?.to ? (
                      startDate.from.getTime() === startDate.to.getTime() 
                        ? format(startDate.from, "MMM dd, yyyy")
                        : `${format(startDate.from, "MMM dd, yyyy")} - ${format(startDate.to, "MMM dd, yyyy")}`
                    ) : startDate?.from ? (
                      format(startDate.from, "MMM dd, yyyy")
                    ) : startDate?.to ? (
                      format(startDate.to, "MMM dd, yyyy")
                    ) : (
                      <span>{t("common.pickADate")}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <DatePicker date={startDate} setDate={(date) => setStartDate(date as DateRange | undefined)} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.expensesReport.filters.endDate")}</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate?.to && !endDate?.from && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate?.from && endDate?.to ? (
                      endDate.from.getTime() === endDate.to.getTime() 
                        ? format(endDate.to, "MMM dd, yyyy")
                        : `${format(endDate.from, "MMM dd, yyyy")} - ${format(endDate.to, "MMM dd, yyyy")}`
                    ) : endDate?.to ? (
                      format(endDate.to, "MMM dd, yyyy")
                    ) : endDate?.from ? (
                      format(endDate.from, "MMM dd, yyyy")
                    ) : (
                      <span>{t("common.pickADate")}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <DatePicker date={endDate} setDate={(date) => setEndDate(date as DateRange | undefined)} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.expensesReport.filters.district")}</label>
              <Select value={selectedDistrict} onValueChange={setSelectedDistrict}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.expensesReport.filters.allDistricts")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.expensesReport.filters.allDistricts")}</SelectItem>
                  {districts.map((d) => (
                    <SelectItem key={d.id} value={d.name}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.expensesReport.filters.branchSector")}</label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.expensesReport.filters.allBranches")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.expensesReport.filters.allBranches")}</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.name}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.expensesReport.filters.group")}</label>
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.expensesReport.filters.allGroups")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.expensesReport.filters.allGroups")}</SelectItem>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id.toString()}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.expensesReport.filters.category")}</label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.expensesReport.filters.allCategories")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.expensesReport.filters.allCategories")}</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {(startDate || endDate || selectedGroup !== "all" || selectedBranch !== "all" || selectedDistrict !== "all" || selectedCategory !== "all") && (
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={clearFilters}>
                {t("pages.expensesReport.filters.clearFilters")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.expensesReport.stats.totalExpenses")}</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalExpenses}</div>
            <p className="text-xs text-muted-foreground mt-1">{t("pages.expensesReport.stats.records")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.expensesReport.stats.totalAmount")}</CardTitle>
            <DollarSign className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {stats.totalAmount.toLocaleString()} RWF
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t("pages.expensesReport.stats.totalExpenditure")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.expensesReport.stats.averageExpense")}</CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalExpenses > 0 
                ? Math.round(stats.totalAmount / stats.totalExpenses).toLocaleString()
                : "0"} RWF
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t("pages.expensesReport.stats.perExpense")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.expensesReport.stats.categories")}</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.uniqueCategories}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t("pages.expensesReport.stats.acrossGroups", { count: stats.uniqueGroups })}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* By Category */}
        <Card>
          <CardHeader>
            <CardTitle>{t("pages.expensesReport.analytics.byCategory")}</CardTitle>
            <CardDescription>{t("pages.expensesReport.analytics.byCategoryDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(categoryStats).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("common.noData")}</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(categoryStats)
                  .sort((a, b) => b[1].amount - a[1].amount)
                  .map(([category, data]) => (
                    <div key={category} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{category}</Badge>
                        <span className="text-sm text-muted-foreground">
                          ({data.count})
                        </span>
                      </div>
                      <div className="text-sm font-medium text-red-600">
                        {Math.round(data.amount).toLocaleString()} RWF
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* By Group */}
        <Card>
          <CardHeader>
            <CardTitle>{t("pages.expensesReport.analytics.topSpendingGroups")}</CardTitle>
            <CardDescription>{t("pages.expensesReport.analytics.topSpendingGroupsDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(groupStats).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("common.noData")}</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(groupStats)
                  .sort((a, b) => b[1].amount - a[1].amount)
                  .slice(0, 5)
                  .map(([group, data]) => (
                    <div key={group} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate max-w-[150px]">
                          {group}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          ({data.count})
                        </span>
                      </div>
                      <div className="text-sm font-medium text-red-600">
                        {Math.round(data.amount).toLocaleString()} RWF
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Monthly Trend */}
        <Card>
          <CardHeader>
            <CardTitle>{t("pages.expensesReport.analytics.monthlyTrend")}</CardTitle>
            <CardDescription>{t("pages.expensesReport.analytics.monthlyTrendDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(monthlyStats).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("common.noData")}</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(monthlyStats)
                  .sort((a, b) => {
                    // Sort by date
                    const dateA = new Date(a[0]);
                    const dateB = new Date(b[0]);
                    return dateB.getTime() - dateA.getTime();
                  })
                  .slice(0, 6)
                  .map(([month, data]) => (
                    <div key={month} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{month}</span>
                        <span className="text-sm text-muted-foreground">
                          ({data.count})
                        </span>
                      </div>
                      <div className="text-sm font-medium text-red-600">
                        {Math.round(data.amount).toLocaleString()} RWF
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("pages.expensesReport.table.title")}</CardTitle>
          <CardDescription>
            {expenses.length === 1 
              ? t("pages.expensesReport.table.description", { count: expenses.length })
              : t("pages.expensesReport.table.description_plural", { count: expenses.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {expensesQuery.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t("pages.expensesReport.table.noExpenses")}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("pages.expensesReport.table.adjustFilters")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("pages.expensesReport.table.date")}</TableHead>
                    <TableHead>{t("pages.expensesReport.table.descriptionColumn")}</TableHead>
                    <TableHead>{t("pages.expensesReport.table.category")}</TableHead>
                    <TableHead>{t("pages.expensesReport.table.group")}</TableHead>
                    <TableHead>{t("pages.expensesReport.table.branch")}</TableHead>
                    <TableHead className="text-right">{t("pages.expensesReport.table.amount")}</TableHead>
                    <TableHead>{t("pages.expensesReport.table.createdBy")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap">
                        {e.date ? format(new Date(e.date), "MMM dd, yyyy") : "—"}
                      </TableCell>
                      <TableCell className="max-w-[300px] truncate">
                        {e.description || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{e.category?.name || "—"}</Badge>
                      </TableCell>
                      <TableCell>{e.group?.name || "—"}</TableCell>
                      <TableCell>{e.group?.branch?.name || "—"}</TableCell>
                      <TableCell className="text-right font-medium text-red-600">
                        {(Number(e.amount) || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>{e.createdBy?.name || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
